import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchPayers, fetchPriorAuthRules, savePriorAuthRule, retirePriorAuthRule } from '../services/api';
import { AdminTableSkeleton } from '../components/Skeleton';
import './Admin.css';
import './PriorAuthRules.css';

// Prior-authorization requirement rules (sync plan Step 10a). ADMIN-ONLY.
//
// A rule says whether ONE payer requires authorization for ONE code, from a date.
// Per payer AND code, because Original Medicare needs no authorization for advanced
// imaging while Medicare Advantage does. Rules are never edited: a change is a new
// rule from a later date, and retiring one makes the requirement "unknown" again —
// which never holds a claim. Every rule records where it came from.

const EMPTY_FORM = {
  payer_id: '',
  code: '',
  code_system: 'CPT',
  requirement: 'required',
  authority_name: '',
  authority_phone: '',
  authority_url: '',
  retro_auth_days: '',
  effective_from: '',
  source_citation: '',
  notes: '',
};

const errorText = (err, fallback) => err.response?.data?.error || fallback;

const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString() : '—');

function inForceToday(rule) {
  const today = new Date().toISOString().slice(0, 10);
  return rule.effective_from <= today && (!rule.effective_to || rule.effective_to > today);
}

export function ruleWindowLabel(rule) {
  if (!rule.effective_to) return `From ${fmtDate(rule.effective_from)}`;
  return `${fmtDate(rule.effective_from)} – ${fmtDate(rule.effective_to)}`;
}

export default function PriorAuthRules() {
  const [rules, setRules] = useState([]);
  const [payers, setPayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [filterPayer, setFilterPayer] = useState('');
  const [filterCode, setFilterCode] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [retiring, setRetiring] = useState(null);
  const [retireDate, setRetireDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchPriorAuthRules({ payerId: filterPayer, code: filterCode.trim() });
      setRules(res.data || []);
    } catch (err) {
      setError(errorText(err, 'Could not load prior-authorization rules.'));
    } finally {
      setLoading(false);
    }
  }, [filterPayer, filterCode]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetchPayers()
      .then((res) => setPayers((res.data || []).filter((p) => !p.is_self_pay_payer)))
      .catch(() => setPayers([]));
  }, []);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const openForm = (rule = null) => {
    setFormError('');
    setForm(rule ? {
      ...EMPTY_FORM,
      payer_id: String(rule.payer_id),
      code: rule.code,
      code_system: rule.code_system,
      requirement: rule.requirement,
      authority_name: rule.authority_name || '',
      authority_phone: rule.authority_phone || '',
      authority_url: rule.authority_url || '',
      retro_auth_days: rule.retro_auth_days ?? '',
    } : EMPTY_FORM);
    setFormOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      const res = await savePriorAuthRule({
        ...form,
        payer_id: Number(form.payer_id),
        retro_auth_days: form.retro_auth_days === '' ? null : Number(form.retro_auth_days),
        effective_from: form.effective_from || undefined,
      });
      setNotice(res.appended === false
        ? 'That rule is already in force — nothing changed.'
        : res.supersededRuleId ? 'Rule saved. The previous rule ends the day this one starts.' : 'Rule saved.');
      setFormOpen(false);
      load();
    } catch (err) {
      setFormError(errorText(err, 'Could not save the rule.'));
    } finally {
      setSaving(false);
    }
  };

  const retire = async () => {
    setSaving(true);
    try {
      await retirePriorAuthRule({
        payer_id: retiring.payer_id, code: retiring.code, effective_to: retireDate || undefined,
      });
      setNotice('Rule retired. From that date the requirement is unknown and no claim is held for it.');
      setRetiring(null);
      load();
    } catch (err) {
      setError(errorText(err, 'Could not retire the rule.'));
      setRetiring(null);
    } finally {
      setSaving(false);
    }
  };

  const openCount = useMemo(() => rules.filter((r) => !r.effective_to).length, [rules]);

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-text">
          <h1>Prior-Authorization Rules</h1>
          <p>
            Whether a payer requires authorization for a code, by date of service. A code with no rule is
            <strong> unknown</strong>, and unknown never holds a claim.
          </p>
        </div>
        <button className="admin-create-btn" onClick={() => openForm()}>Add rule</button>
      </div>

      <div className="admin-toolbar par-toolbar">
        <select className="par-select" value={filterPayer} onChange={(e) => setFilterPayer(e.target.value)} aria-label="Filter by payer">
          <option value="">All payers</option>
          {payers.map((p) => <option key={p.payer_id} value={p.payer_id}>{p.name}</option>)}
        </select>
        <input
          className="admin-search par-code-filter"
          value={filterCode}
          onChange={(e) => setFilterCode(e.target.value)}
          placeholder="Code, e.g. 70553"
          aria-label="Filter by code"
        />
        <span className="admin-count">{openCount} open rule{openCount === 1 ? '' : 's'}</span>
      </div>

      {notice && <div className="par-notice" role="status">{notice}</div>}
      {error && <div className="admin-modal-alert error">{error}</div>}

      <div className="admin-table-card par-table-card">
        {loading ? (
          <AdminTableSkeleton columns={8} rows={5} avatarFirstCol={false} />
        ) : rules.length === 0 ? (
          <div className="par-empty">
            No rules yet. Until a payer and code have a rule, the requirement is unknown and no claim is held for it.
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Payer</th>
                <th>Code</th>
                <th>Requirement</th>
                <th>Reviewed by</th>
                <th>Retro-auth</th>
                <th>In force</th>
                <th>Source</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} className={inForceToday(rule) ? '' : 'par-row-inactive'}>
                  <td>{rule.payer_name}</td>
                  <td className="par-code">{rule.code}<span className="par-system">{rule.code_system}</span></td>
                  <td>
                    <span className={`par-req par-req--${rule.requirement}`}>
                      {rule.requirement === 'required' ? 'Required' : 'Not required'}
                    </span>
                  </td>
                  <td>
                    {rule.authority_name || <span className="par-muted">The payer</span>}
                    {rule.authority_phone && <div className="par-muted">{rule.authority_phone}</div>}
                  </td>
                  <td>{rule.retro_auth_days != null ? `${rule.retro_auth_days} days` : <span className="par-muted">—</span>}</td>
                  <td>{ruleWindowLabel(rule)}</td>
                  <td className="par-citation" title={rule.source_citation}>{rule.source_citation}</td>
                  <td className="par-actions">
                    {!rule.effective_to && (
                      <>
                        <button className="par-link" onClick={() => openForm(rule)}>Change</button>
                        <button className="par-link" onClick={() => { setRetiring(rule); setRetireDate(''); }}>Retire</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {formOpen && (
        <div className="admin-modal-overlay">
          <form className="admin-confirm-dialog par-dialog" onSubmit={save}>
            <h3>{form.code ? 'Change a rule' : 'Add a rule'}</h3>
            <p className="par-muted">
              A change starts a new rule from its date; the rule before it ends that day. History is kept.
            </p>
            {formError && <div className="admin-modal-alert error">{formError}</div>}
            <div className="par-grid">
              <label>Payer
                <select value={form.payer_id} onChange={update('payer_id')} required>
                  <option value="">Choose a payer</option>
                  {payers.map((p) => <option key={p.payer_id} value={p.payer_id}>{p.name}</option>)}
                </select>
              </label>
              <label>Code
                <input value={form.code} onChange={update('code')} placeholder="70553" required />
              </label>
              <label>Code system
                <select value={form.code_system} onChange={update('code_system')}>
                  <option value="CPT">CPT</option>
                  <option value="HCPCS">HCPCS</option>
                </select>
              </label>
              <label>Requirement
                <select value={form.requirement} onChange={update('requirement')}>
                  <option value="required">Authorization required</option>
                  <option value="not_required">Not required</option>
                </select>
              </label>
              <label>Reviewed by (if not the payer)
                <input value={form.authority_name} onChange={update('authority_name')} placeholder="e.g. a radiology benefit manager" />
              </label>
              <label>Reviewer phone
                <input value={form.authority_phone} onChange={update('authority_phone')} />
              </label>
              <label className="par-wide">Reviewer portal URL
                <input value={form.authority_url} onChange={update('authority_url')} />
              </label>
              <label>Retro-auth window (days)
                <input type="number" min="0" value={form.retro_auth_days} onChange={update('retro_auth_days')} placeholder="Leave blank if none" />
              </label>
              <label>In force from
                <input type="date" value={form.effective_from} onChange={update('effective_from')} />
              </label>
              <label className="par-wide">Source (required)
                <input value={form.source_citation} onChange={update('source_citation')} placeholder="Policy URL and the date you read it" required />
              </label>
              <label className="par-wide">Notes
                <input value={form.notes} onChange={update('notes')} />
              </label>
            </div>
            <div className="admin-confirm-actions">
              <button type="button" className="admin-modal-btn-cancel" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</button>
              <button type="submit" className="admin-create-btn" disabled={saving}>{saving ? 'Saving…' : 'Save rule'}</button>
            </div>
          </form>
        </div>
      )}

      {retiring && (
        <div className="admin-modal-overlay">
          <div className="admin-confirm-dialog">
            <h3>Retire {retiring.code} for {retiring.payer_name}?</h3>
            <p className="par-muted">
              From the date below the requirement is unknown, and no claim is held for it. The rule stays in the history.
            </p>
            <label className="par-retire-date">Retire from
              <input type="date" value={retireDate} onChange={(e) => setRetireDate(e.target.value)} />
            </label>
            <div className="admin-confirm-actions">
              <button className="admin-modal-btn-cancel" onClick={() => setRetiring(null)} disabled={saving}>Cancel</button>
              <button className="admin-confirm-danger" onClick={retire} disabled={saving}>Retire rule</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
